/**
 * Agent & Allocator API Service
 *
 * workspace 生命周期通过 agent 服务管理（带 JWT auth）
 * 消息通信通过 allocator 服务直连
 *
 * 基于 agent-openapi.json + allocator-openapi.json 规范
 */

import { apiFetch } from './client'
import { API_BASE } from './config'

// ── 服务地址配置 ──

// Agent 服务：workspace CRUD、assistant 等（走 apiFetch 统一 JWT）
const AGENT_BASE_URL = import.meta.env?.VITE_AGENT_BASE_URL || API_BASE

// Allocator 服务：filesystem、message 等（直连）
const ALLOCATOR_BASE_URL = import.meta.env?.VITE_ALLOCATOR_BASE_URL
  || `${API_BASE}/api/allocator`

// ── 类型定义 ──

export interface AllocateInstanceResponse {
  instanceId: string
}

export interface InstanceItem {
  instanceId: string
}

export interface GetInstanceListResponse {
  items: InstanceItem[]
  total: number
  page: number
  pageSize: number
}

export interface SendMessageRequest {
  instanceId: string
  message: string
}

export interface MessageEvent {
  type: 'message' | 'end'
  content?: string
}

// ── Workspace API（agent 服务） ──

/**
 * 创建工作区（原 allocateInstance）
 * POST /api/agent/workspace/create
 */
export async function allocateInstance(): Promise<AllocateInstanceResponse> {
  return apiFetch<AllocateInstanceResponse>('/api/agent/workspace/create', {
    method: 'POST',
    body: {},
  })
}

/**
 * 获取工作区列表（原 getInstanceList）
 * GET /api/agent/workspace/list
 */
export async function getInstanceList(): Promise<InstanceItem[]> {
  return apiFetch<InstanceItem[]>('/api/agent/workspace/list')
}

/**
 * 删除工作区
 * POST /api/agent/workspace/delete
 */
export async function deleteWorkspace(instanceId: string): Promise<void> {
  await apiFetch<void>('/api/agent/workspace/delete', {
    method: 'POST',
    body: { instanceId },
  })
}

// ── Message API（allocator 服务，直连） ──

/**
 * 发送消息
 * POST /api/allocator/message.send
 */
export async function sendMessage(req: SendMessageRequest): Promise<void> {
  const res = await fetch(`${ALLOCATOR_BASE_URL}/message.send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `发送消息失败 (${res.status})`)
  }

  const json = await res.json()
  if (json.code !== 0) {
    throw new Error(json.message || 'Failed to send message')
  }
}

/**
 * 读取消息流（SSE）
 * GET /api/allocator/message.read
 *
 * 解析 Claude Code 消息格式：
 * - { type: "assistant", message: { content: [{ type: "text", text: "..." }] } }
 * - { type: "result", result: "..." }
 * - { type: "end" }
 *
 * @param instanceId 实例 ID
 * @param history 是否读取历史消息
 * @param onMessage 消息回调
 * @param onEnd 流结束回调
 * @param onError 错误回调
 * @returns 关闭连接的函数
 */
export function readMessages(
  instanceId: string,
  options: {
    history?: boolean
    onMessage: (content: string) => void
    onEnd?: () => void
    onError?: (error: Error) => void
  }
): () => void {
  const { history = false, onMessage, onEnd, onError } = options

  const params = new URLSearchParams({
    instanceId,
    history: String(history),
  })

  const url = `${ALLOCATOR_BASE_URL}/message.read?${params.toString()}`

  const eventSource = new EventSource(url)

  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)

      // assistant 消息：从 message.content 中提取 text
      if (data.type === 'assistant' && data.message?.content) {
        const content = data.message.content
        if (Array.isArray(content)) {
          for (const item of content) {
            if (item.type === 'text' && item.text) {
              onMessage(item.text)
            } else if (item.type === 'thinking' && item.thinking) {
              // 思考过程可以选择显示或忽略
              // onMessage(item.thinking)
            }
          }
        }
      }
      // result 消息：最终结果
      else if (data.type === 'result' && data.result) {
        // result 已经是完整的回复，不需要再处理
      }
      // end 消息：流结束
      else if (data.type === 'end') {
        onEnd?.()
        eventSource.close()
      }
      // 兼容旧格式
      else if (data.type === 'message' && data.content) {
        onMessage(data.content)
      }
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error('Failed to parse message'))
    }
  }

  eventSource.onerror = () => {
    onError?.(new Error('SSE connection error'))
    eventSource.close()
  }

  return () => eventSource.close()
}

// ── 导出 allocator base URL（供 SSE service 和 filesystem 使用） ──

export { ALLOCATOR_BASE_URL, AGENT_BASE_URL }
